import type {
  Camera,
  CameraState,
  SessionState,
  CameraDisplayState,
  SessionDisplayState,
} from '../types';

/**
 * SessionDisplay - Display model for Camera with computed properties
 * Separates business logic from raw data model
 */
export class SessionDisplay {
  private camera: Camera;

  constructor(camera: Camera) {
    this.camera = camera;
  }

  // Raw data accessors
  get id(): string {
    return this.camera.id;
  }

  get name(): string {
    return this.camera.name;
  }

  get cameraState(): CameraState {
    return this.camera.cameraState;
  }

  get sessionState(): SessionState {
    return this.camera.sessionState;
  }

  get count(): number {
    return this.camera.count;
  }

  get weight(): number {
    return this.camera.weight;
  }

  get duration(): string {
    return this.camera.duration;
  }

  get weightPerFish(): number | undefined {
    return this.camera.weightPerFish;
  }

  get error(): { cameraError?: string; sessionError?: string } | undefined {
    return this.camera.error;
  }

  // Computed display states
  get cameraDisplayState(): CameraDisplayState {
    switch (this.camera.cameraState) {
      case 'disconnected':
        return 'offline';
      case 'connecting':
        return 'transitioning';
      case 'connected':
        return 'ready';
      case 'playing':
        return 'streaming';
      case 'error':
        return 'transitioning';
      default:
        return 'offline';
    }
  }

  get sessionDisplayState(): SessionDisplayState {
    switch (this.camera.sessionState) {
      case 'idle':
        return 'idle';
      case 'ready':
        return 'idle';
      case 'starting':
        return 'starting';
      case 'counting':
        return 'active';
      case 'stopping':
        return 'stopping';
      case 'error':
        return 'error';
      default:
        return 'idle';
    }
  }

  // Computed boolean flags
  get isConnected(): boolean {
    return this.camera.cameraState === 'connected' || this.camera.cameraState === 'playing';
  }

  get isStreaming(): boolean {
    return this.camera.cameraState === 'playing';
  }

  get isCounting(): boolean {
    return this.camera.sessionState === 'counting';
  }

  get isTransitioning(): boolean {
    return (
      this.camera.cameraState === 'connecting' ||
      this.camera.sessionState === 'starting' ||
      this.camera.sessionState === 'stopping'
    );
  }

  get hasError(): boolean {
    return this.camera.cameraState === 'error' || this.camera.sessionState === 'error';
  }

  // Business logic conditions
  get canStartSession(): boolean {
    return this.camera.cameraState === 'playing' && this.camera.sessionState === 'ready';
  }

  get canStopSession(): boolean {
    return this.camera.sessionState === 'counting' || this.camera.sessionState === 'starting';
  }

  get shouldShowCountingIndicator(): boolean {
    return this.camera.sessionState === 'counting';
  }

  get shouldShowRecordingButton(): boolean {
    return this.isCounting;
  }

  get shouldShowStartButton(): boolean {
    return this.canStartSession;
  }

  get shouldShowLoadingSpinner(): boolean {
    return this.isTransitioning;
  }

  // Utility methods
  getStatusText(): string {
    if (this.hasError) {
      return 'Error';
    }

    if (this.isCounting) {
      return Math.floor(this.count).toLocaleString();
    }

    if (this.camera.sessionState === 'starting') {
      return 'Starting...';
    }

    if (this.camera.sessionState === 'stopping') {
      return 'Stopping..';
    }

    if (this.canStartSession) {
      return 'Ready';
    }

    if (!this.isConnected) {
      return 'Disconnected';
    }

    return 'Stopped';
  }

  // Get raw camera object (for backward compatibility)
  getRawCamera(): Camera {
    return this.camera;
  }

  // Create from raw camera
  static fromCamera(camera: Camera): SessionDisplay {
    return new SessionDisplay(camera);
  }

  // Create array from raw cameras
  static fromCameras(cameras: Camera[]): SessionDisplay[] {
    return cameras.map((camera) => new SessionDisplay(camera));
  }
}

/**
 * Helper function to convert Camera to SessionDisplay
 * Alias for SessionDisplay.fromCamera for convenience
 */
export function toSessionDisplay(camera: Camera): SessionDisplay {
  return SessionDisplay.fromCamera(camera);
}

/**
 * Helper function to convert Camera[] to SessionDisplay[]
 * Alias for SessionDisplay.fromCameras for convenience
 */
export function toSessionDisplays(cameras: Camera[]): SessionDisplay[] {
  return SessionDisplay.fromCameras(cameras);
}
